import { InlineMessage } from '@crowdex/ui/components/feedback';
import { toast } from '@crowdex/ui/components/toaster';
import { cn } from '@crowdex/ui/lib/utils';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ConnectionsSettingsSection, PluginsSettingsSection } from '@/components/plugins-page';
import { AccountSettingsSection } from '@/components/settings-page/account-settings-section';
import { AgentsSettingsSection } from '@/components/settings-page/agents-settings-section';
import { ComputerUseSettingsSection } from '@/components/settings-page/computer-use-settings-section';
import {
  GeneralSettingsSection,
  isThemesPath,
} from '@/components/settings-page/general-settings-section';
import { IntegrationsSettingsSection } from '@/components/settings-page/integrations-settings-section';
import { KeyboardShortcutsSettingsSection } from '@/components/settings-page/keyboard-shortcuts-settings-section';
import { ModelsSettingsSection } from '@/components/settings-page/model-settings';
import { NotificationsSettingsSection } from '@/components/settings-page/notifications-settings-section';
import { PersonaSettingsView, PersonasGroup } from '@/components/settings-page/personas-settings';
import { SettingsPageHeader } from '@/components/settings-page/setting-controls';
import { SettingsMobileList } from '@/components/settings-page/settings-mobile-list';
import {
  SettingsNavigation,
  type SettingsNavigationModel,
} from '@/components/settings-page/settings-navigation';
import {
  defaultAgentLabel,
  useSettingsCounts,
  useVersionLine,
} from '@/components/settings-page/settings-summary';
import { VoiceSettingsSection } from '@/components/settings-page/voice-settings-section';
import { WorktreeSettingsSection } from '@/components/settings-page/worktree-settings-section';
import { useAccountAgentProfiles } from '@/hooks/use-account-agent-profiles';
import { useMediaQuery } from '@/hooks/use-media-query';
import {
  type AppSettings,
  applyAppSettings,
  DEFAULT_APP_SETTINGS,
  readAppSettings,
  resolveDesktopTheme,
  SETTINGS_CHANGED_EVENT,
  writeAppSettings,
} from '@/lib/app-settings';
import { notifyCloudLinkChanged, unlinkCloud } from '@/lib/cloud-link';
import { DESKTOP_LAYOUT_QUERY } from '@/lib/desktop-shell/desktop-layout';
import { electronPlatform, isElectronDesktop } from '@/lib/electron-bridge';
import { isLocalMachine } from '@/lib/local-runtime';
import { useLibraryPage, useRouter } from '@/lib/navigation';
import { type PluginSection, pluginsPath } from '@/lib/plugins/plugin-sections';
import { resolvePreferencesDisplayName } from '@/lib/settings-page/initial-preferences';
import {
  personaPath,
  type SettingsSection,
  sectionInfo,
  settingsSectionPath,
} from '@/lib/settings-page/sections';
import { settingsPreview } from '@/lib/settings-page/settings-preview';
import { signOutOfCloud } from '@/lib/sign-out';
import { useAppPath } from '@/vite/navigation-store';

/** How long a typed setting waits for the typing to pause before it is kept. */
const TYPING_PAUSE_MS = 400;

/** Whether an open menu, list or dialog takes Esc before the page does. */
function overlayOpen(): boolean {
  return (
    document.querySelector(
      '[role="dialog"][data-state="open"], [role="listbox"], [role="menu"][data-state="open"], [data-slot="select-content"]',
    ) !== null
  );
}

/** A field Esc must not take the typed text out of: there, Esc only lets go of the focus. */
function isTextField(element: HTMLElement): boolean {
  if (element.isContentEditable || element instanceof HTMLTextAreaElement) return true;
  if (!(element instanceof HTMLInputElement)) return false;
  return !NON_TEXT_INPUTS.has(element.type);
}
const NON_TEXT_INPUTS = new Set([
  'button',
  'checkbox',
  'color',
  'file',
  'image',
  'radio',
  'range',
  'reset',
  'submit',
]);

/**
 * Settings (plan §2.14, D16–D19b, D40–D44): a full page with its own section column on a
 * computer, a grouped list with one sub-page per section on a phone. Every change applies at once.
 */
export function SettingsPage({
  email,
  initialSection = null,
  initialProfile = null,
  pluginView = 'browse',
  plugin = null,
  connection = null,
  name,
}: {
  email: string;
  /** The section the address names; none opens General (the phone: its list). */
  initialSection?: SettingsSection | null;
  /** A persona of Settings › Personas the address opens (`&profile=<id>`). */
  initialProfile?: string | null;
  /** Settings › Plugins' view (`&view=`): Browse, Installed or Custom. */
  pluginView?: PluginSection;
  /** The plugin whose page Settings › Plugins opens (`&plugin=<name>`). */
  plugin?: string | null;
  /** The connection Settings › Connections signs in to again (`&connection=<id>`). */
  connection?: string | null;
  name: string;
}) {
  const router = useRouter();
  const library = useLibraryPage();
  const phone = !useMediaQuery(DESKTOP_LAYOUT_QUERY);
  const local = isLocalMachine() || settingsPreview()?.local === true;
  const [preferences, setPreferences] = useState(DEFAULT_APP_SETTINGS);
  const [saveError, setSaveError] = useState<string | null>(null);
  const counts = useSettingsCounts();
  const versionLine = useVersionLine();
  const agentProfiles = useAccountAgentProfiles();
  const section: SettingsSection | null = initialSection ?? (phone ? null : 'general');

  useEffect(() => {
    const next = resolvePreferencesDisplayName(readAppSettings(), name, email);
    setPreferences(next);
    applyAppSettings(next);
  }, [email, name]);
  useEffect(() => {
    if (preferences.theme !== 'system') return;
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const sync = () => applyAppSettings(preferences);
    media.addEventListener('change', sync);
    return () => media.removeEventListener('change', sync);
  }, [preferences]);

  const current = useRef(preferences);
  current.current = preferences;
  const pendingWrite = useRef<number | null>(null);
  const write = useCallback((next: AppSettings) => {
    pendingWrite.current = null;
    void writeAppSettings(next).then(
      () => window.dispatchEvent(new Event(SETTINGS_CHANGED_EVENT)),
      () => setSaveError('Could not save settings on this device.'),
    );
  }, []);
  // A text being typed is kept once the typing pauses, and before the page goes.
  useEffect(
    () => () => {
      if (pendingWrite.current === null) return;
      window.clearTimeout(pendingWrite.current);
      write(current.current);
    },
    [write],
  );

  /** Applies and keeps a setting at once: there is no Save button. */
  const update = useCallback(
    <K extends keyof AppSettings>(
      key: K,
      value: AppSettings[K],
      options: { debounce?: boolean } = {},
    ) => {
      const next = { ...current.current, [key]: value };
      current.current = next;
      setPreferences(next);
      applyAppSettings(next);
      setSaveError(null);
      if (pendingWrite.current !== null) window.clearTimeout(pendingWrite.current);
      if (options.debounce)
        pendingWrite.current = window.setTimeout(() => write(current.current), TYPING_PAUSE_MS);
      else write(next);
    },
    [write],
  );

  const leave = useCallback(() => {
    if (phone && window.history.length > 1) window.history.back();
    else library.leave();
  }, [library, phone]);

  // A sub-page (a plugin's page, a persona) goes up to its list; at the top, Settings has none.
  const profileOpen = section === 'personas' && initialProfile !== null;
  const pluginOpen = section === 'plugins' && plugin !== null;
  const themesOpen = isThemesPath(useAppPath());
  const up = profileOpen
    ? () => router.replace(settingsSectionPath('personas'))
    : pluginOpen
      ? () => router.push(pluginsPath(pluginView))
      : themesOpen
        ? () => router.replace(settingsSectionPath('general'))
        : null;
  const upRef = useRef(up);
  upRef.current = up;

  // Esc, once nothing open over the page takes it: a text field only lets go of the focus (what
  // is typed stays), a sub-page goes back one level, the top of Settings goes back to the app.
  useEffect(() => {
    if (phone) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented || overlayOpen()) return;
      event.preventDefault();
      const target = event.target;
      if (target instanceof HTMLElement && isTextField(target)) {
        target.blur();
        return;
      }
      if (upRef.current !== null) upRef.current();
      else library.leave();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [library, phone]);

  const select = (next: SettingsSection) => {
    // The phone pushes a sub-page (its back returns to the list); the column replaces the section.
    if (phone) router.push(settingsSectionPath(next));
    else router.replace(settingsSectionPath(next));
  };
  const signOut = () => {
    if (local) {
      // A machine's account is its link to Crowdex Cloud: the app asks to link it again.
      if (
        !window.confirm(
          'Sign out of Crowdex Cloud on this computer? Its conversations stay here; you sign in again to use the app.',
        )
      )
        return;
      void unlinkCloud().then(
        () => {
          toast.info('Signed out of Crowdex Cloud');
          notifyCloudLinkChanged();
        },
        (reason: unknown) => toast.error('Could not sign out', String(reason)),
      );
      return;
    }
    if (!window.confirm('Sign out of Crowdex on this device?')) return;
    // The cloud's sign-out also ends the Crowlingo session, which then comes back to `/sign-in`.
    void signOutOfCloud();
  };
  const openProfile = (id: string) => router.push(personaPath(id));
  // Plugins and Connections head their own page (views, Marketplaces, Connect an account).
  const ownHeader = profileOpen || section === 'plugins' || section === 'connections';
  // Their lists and cards take the width a plugin's page needs.
  const wide = section === 'plugins' || section === 'connections';

  const displayName = preferences.displayName || name || email;
  const model: SettingsNavigationModel = {
    displayName,
    details: {
      agents: defaultAgentLabel(preferences.defaultAgent),
      ...(agentProfiles.profiles.length > 0
        ? { personas: String(agentProfiles.profiles.length) }
        : {}),
    },
    reconnect: counts.reconnect,
    versionLine,
    local,
  };
  const desktopWindow = isElectronDesktop();
  const macWindow = desktopWindow && electronPlatform() === 'macos';
  const theme = resolveDesktopTheme(preferences.theme);

  const content =
    section === null ? null : (
      <>
        {!ownHeader && (
          <SettingsPageHeader
            // The account's own header says who it is (D44): its title only.
            description={section === 'account' ? undefined : sectionInfo(section).description}
            title={sectionInfo(section).title}
          />
        )}
        {saveError && (
          <InlineMessage className="mb-4" tone="error">
            {saveError}
          </InlineMessage>
        )}
        {section === 'general' && (
          <GeneralSettingsSection
            onOpenAgents={() => select('agents')}
            onUpdate={update}
            phone={phone}
            preferences={preferences}
          />
        )}
        {profileOpen && initialProfile !== null && (
          <PersonaSettingsView
            onBack={() => router.replace(settingsSectionPath('personas'))}
            profileId={initialProfile}
            profiles={agentProfiles.profiles}
            replace={agentProfiles.replace}
          />
        )}
        {section === 'personas' && !profileOpen && (
          <PersonasGroup
            error={agentProfiles.error}
            onOpen={(profile) => openProfile(profile.id)}
            profiles={agentProfiles.profiles}
          />
        )}
        {section === 'agents' && (
          <AgentsSettingsSection
            agentIds={preferences.agentIds}
            agentRuntimeOverrides={preferences.agentRuntimeOverrides}
            defaultAgent={preferences.defaultAgent}
            onAgentIdsChange={(agentIds) => update('agentIds', agentIds)}
            onAgentRuntimeOverridesChange={(agentRuntimeOverrides) =>
              update('agentRuntimeOverrides', agentRuntimeOverrides)
            }
            onDefaultAgentChange={(defaultAgent) => update('defaultAgent', defaultAgent)}
          />
        )}
        {section === 'models' && <ModelsSettingsSection />}
        {section === 'voice' && <VoiceSettingsSection phone={phone} />}
        {section === 'notifications' && <NotificationsSettingsSection phone={phone} />}
        {section === 'worktrees' && <WorktreeSettingsSection local={local} phone={phone} />}
        {section === 'computer-use' && <ComputerUseSettingsSection />}
        {section === 'keyboard-shortcuts' && <KeyboardShortcutsSettingsSection />}
        {section === 'account' && (
          <AccountSettingsSection
            displayName={preferences.displayName}
            email={email}
            onDisplayNameChange={(value) => update('displayName', value, { debounce: true })}
            onSignOut={signOut}
          />
        )}
        {section === 'integrations' && <IntegrationsSettingsSection />}
        {section === 'plugins' && (
          <PluginsSettingsSection phone={phone} pluginName={plugin} section={pluginView} />
        )}
        {section === 'connections' && (
          <ConnectionsSettingsSection connection={connection} phone={phone} />
        )}
      </>
    );

  if (phone)
    return (
      <main
        // Inside the phone shell, which already keeps clear of the notch and the home indicator:
        // not a second time (`.desktop-shell` pads by the safe areas).
        className="desktop-shell h-full overflow-y-auto bg-background p-0! text-foreground"
        data-compact={preferences.compactMode || undefined}
        data-theme={theme}
      >
        {section === null ? (
          <SettingsMobileList model={model} onSelect={select} onSignOut={signOut} />
        ) : (
          <div className="mx-auto w-full max-w-xl px-4 pt-3 pb-10">{content}</div>
        )}
      </main>
    );

  // The whole window: Settings replaces the sidebar too (D16), above the shell's own frame.
  return createPortal(
    <main
      className="desktop-shell fixed inset-0 z-panel flex overflow-hidden bg-background text-foreground"
      data-compact={preferences.compactMode || undefined}
      data-theme={theme}
    >
      <div className={cn('flex h-full shrink-0 border-hairline border-r', macWindow && 'pt-7')}>
        {macWindow && (
          <div
            aria-hidden
            className="absolute top-0 left-0 h-7 w-64 bg-desktop-sidebar"
            data-electron-drag-region
          />
        )}
        <SettingsNavigation
          active={section ?? 'general'}
          model={model}
          onBack={leave}
          onSelect={select}
          onSignOut={signOut}
        />
      </div>
      {desktopWindow && (
        // The window moves by the strip above the content; Windows' and Linux' controls sit on it.
        <div
          aria-hidden
          className="absolute top-0 right-0 left-64 h-12"
          data-electron-drag-region
        />
      )}
      <section className="min-w-0 flex-1 overflow-y-auto">
        <div
          className={cn(
            'mx-auto w-full px-8 pt-10 pb-16',
            wide ? 'max-w-5xl' : 'max-w-170',
            desktopWindow && 'pt-14',
          )}
        >
          {content}
        </div>
      </section>
    </main>,
    document.body,
  );
}
